<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import CreditOverviewAPI from 'dashboard/api/creditOverview';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Todos os créditos da empresa numa tela: franquias que renovam (com o prazo da próxima
// renovação) e créditos comprados, que não expiram. Abrir a tela só lê os saldos.
const router = useRouter();
const overview = ref(null);
const isLoading = ref(true);
const hasError = ref(false);

const numberFormat = new Intl.NumberFormat('pt-BR');
const formatNumber = value => numberFormat.format(Number(value || 0));
const formatDate = value =>
  value ? new Date(`${value}T12:00:00`).toLocaleDateString('pt-BR') : '';

const renewalText = item => {
  if (!item.renews_at) return item.renewal;
  const days = Number(item.days_to_renewal);
  let when = `em ${days} dias`;
  if (days === 0) when = 'hoje';
  if (days === 1) when = 'amanhã';
  return `Renova ${when} (${formatDate(item.renews_at)}) · ${item.renewal}`;
};

const usagePercent = item =>
  item.unlimited || !item.total
    ? 0
    : Math.min(100, Math.round((Number(item.used) / Number(item.total)) * 100));

const franchises = computed(() => overview.value?.franchises || []);
const purchased = computed(() => overview.value?.purchased || []);

const load = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await CreditOverviewAPI.get();
    overview.value = data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const openMarketplace = () => router.push({ name: 'talkvia_shop' });

onMounted(load);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="mx-auto grid w-full max-w-6xl gap-6 pb-8">
    <header
      class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
    >
      <div class="flex items-start gap-3">
        <span
          class="grid size-11 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <span class="i-hugeicons-wallet-01 size-5" />
        </span>
        <div>
          <h1 class="m-0 text-2xl font-semibold text-n-slate-12">
            Meus créditos
          </h1>
          <p class="mb-0 mt-1 text-sm text-n-slate-11">
            Tudo o que sua empresa tem disponível agora: franquias do plano e
            dos serviços, com o prazo da renovação, e créditos comprados.
          </p>
        </div>
      </div>
      <div class="flex gap-2">
        <NextButton
          v-tooltip.top="'Atualizar'"
          aria-label="Atualizar"
          icon="i-lucide-refresh-cw"
          color="slate"
          variant="ghost"
          size="sm"
          :is-loading="isLoading"
          @click="load"
        />
        <NextButton
          label="Comprar créditos"
          icon="i-lucide-shopping-cart"
          size="sm"
          @click="openMarketplace"
        />
      </div>
    </header>

    <div
      v-if="isLoading && !overview"
      class="grid gap-3 md:grid-cols-2 xl:grid-cols-3"
      aria-busy="true"
    >
      <div
        v-for="index in 3"
        :key="index"
        class="h-36 animate-pulse rounded-xl border border-n-weak bg-n-alpha-1"
      />
    </div>

    <div
      v-else-if="hasError"
      class="grid place-items-center gap-3 rounded-xl border border-dashed border-n-weak p-8 text-center"
    >
      <p class="mb-0 text-sm text-n-slate-11">
        Não foi possível carregar os créditos.
      </p>
      <NextButton
        label="Tentar novamente"
        color="slate"
        variant="outline"
        size="sm"
        @click="load"
      />
    </div>

    <template v-else-if="overview">
      <section class="grid gap-3" aria-labelledby="credits-franchises">
        <h2
          id="credits-franchises"
          class="m-0 text-xs font-semibold uppercase tracking-[0.12em] text-n-slate-10"
        >
          Franquias que renovam
        </h2>
        <div
          v-if="franchises.length"
          class="grid gap-3 md:grid-cols-2 xl:grid-cols-3"
        >
          <article
            v-for="item in franchises"
            :key="item.key"
            class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
          >
            <div class="flex items-start gap-3">
              <span
                class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
              >
                <span :class="item.icon" class="size-4" />
              </span>
              <p class="mb-0 min-w-0 text-sm font-medium text-n-slate-12">
                {{ item.title }}
              </p>
            </div>
            <div>
              <strong
                class="text-2xl font-semibold tabular-nums"
                :class="
                  !item.unlimited && !item.available
                    ? 'text-n-ruby-11'
                    : 'text-n-slate-12'
                "
              >
                {{
                  item.unlimited ? 'Ilimitado' : formatNumber(item.available)
                }}
              </strong>
              <span v-if="!item.unlimited" class="ml-1 text-sm text-n-slate-11">
                de {{ formatNumber(item.total) }} disponíveis
              </span>
            </div>
            <div
              v-if="!item.unlimited"
              class="h-1.5 overflow-hidden rounded-full bg-n-alpha-2"
              role="progressbar"
              :aria-valuenow="usagePercent(item)"
              aria-valuemin="0"
              aria-valuemax="100"
              :aria-label="`${formatNumber(item.used)} usados`"
            >
              <span
                class="block h-full rounded-full"
                :class="usagePercent(item) >= 90 ? 'bg-n-ruby-9' : 'bg-n-brand'"
                :style="{ width: `${usagePercent(item)}%` }"
              />
            </div>
            <p
              class="mb-0 mt-auto flex items-start gap-1.5 text-xs leading-5 text-n-slate-11"
            >
              <span class="i-lucide-calendar-clock mt-0.5 size-3.5 shrink-0" />
              {{ renewalText(item) }}
            </p>
          </article>
        </div>
        <p v-else class="mb-0 text-sm text-n-slate-11">
          Sua empresa não tem franquias com renovação no momento.
        </p>
      </section>

      <section class="grid gap-3" aria-labelledby="credits-purchased">
        <h2
          id="credits-purchased"
          class="m-0 text-xs font-semibold uppercase tracking-[0.12em] text-n-slate-10"
        >
          Créditos comprados · não expiram
        </h2>
        <div
          v-if="purchased.length"
          class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
        >
          <article
            v-for="item in purchased"
            :key="item.key"
            class="flex flex-col gap-1 rounded-xl border border-n-weak bg-n-solid-1 p-4"
          >
            <p class="mb-0 text-sm text-n-slate-11">{{ item.title }}</p>
            <strong
              class="text-2xl font-semibold tabular-nums"
              :class="item.balance ? 'text-n-slate-12' : 'text-n-slate-11'"
            >
              {{ formatNumber(item.balance) }}
            </strong>
            <span class="text-xs text-n-slate-10">créditos em saldo</span>
          </article>
        </div>
        <p v-else class="mb-0 text-sm text-n-slate-11">
          Nenhum crédito comprado ainda.
        </p>
      </section>
    </template>
  </div>
</template>
